import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, ShieldCheck, ShieldAlert, RotateCw, Copy, Check } from "lucide-react";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { listKeys, rotateKey } from "../../api/endpoints";
import { toKeyView, type KeyView } from "../../api/adapters";
import { useApiFallback } from "../../hooks/useApiFallback";
import { ApiError } from "../../api/types";

const MOCK_KEYS: KeyView[] = [
  { id: "corp-2026-A", name: "Корпоративный ключ 2026/A", issued: "01.04.2026", expires: "01.04.2028", status: "active",  sha256: "a4:91:e8:7c:5b:2d:0f:6a:11:38:9c:42:db:e5:13:74", signsApps: 127 },
  { id: "corp-2025-A", name: "Корпоративный ключ 2025/A", issued: "15.03.2025", expires: "15.03.2027", status: "revoked", sha256: "f1:22:43:90:8e:1d:7c:55:0b:e9:13:8a:c4:fa:32:11", signsApps: 0 },
];

export default function KeysPage() {
  const push = useToast();
  const [rotateOpen, setRotateOpen] = useState(false);

  const keysQ = useQuery({ queryKey: ["keys"], queryFn: listKeys });
  const keys = useApiFallback<KeyView[]>(
    keysQ.data?.map(toKeyView),
    keysQ.isError,
    MOCK_KEYS,
  );
  const activeKey = keys.value.find((k) => k.status === "active") ?? keys.value[0];

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Ключи подписи
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Корпоративный ключ переподписывает каждое приложение перед раздачей по локальным серверам.
          </div>
        </div>
        <Btn icon={RotateCw} variant="primary" onClick={() => setRotateOpen(true)}>
          Ротация ключа
        </Btn>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Stat
          label="Активный ключ"
          value={
            <span className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[var(--color-success)]" />
              {activeKey?.id ?? "—"}
            </span>
          }
          sub="используется для всех новых публикаций"
        />
        <Stat
          label="Хранение"
          value={activeKey ? "HSM" : "—"}
          sub={activeKey ? "аппаратный модуль безопасности" : "ключей нет"}
        />
        <Stat
          label="Подписано версий"
          value={<span>{keys.value.reduce((s, k) => s + k.signsApps, 0).toLocaleString("ru-RU")}</span>}
          sub="за всё время"
        />
      </div>

      <section className={"mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 " + (keysQ.isLoading ? "opacity-70" : "")}>
        <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Все ключи</h2>
        <ul className="mt-4 space-y-3">
          {keys.value.map((k) => (
            <li key={k.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span
                    className={
                      "grid h-9 w-9 shrink-0 place-items-center rounded-lg " +
                      (k.status === "active"
                        ? "bg-[var(--color-success-bg)] text-[var(--color-success)]"
                        : "bg-[var(--color-danger-bg)] text-[var(--color-danger)]")
                    }
                  >
                    {k.status === "active" ? <KeyRound size={16} /> : <ShieldAlert size={16} />}
                  </span>
                  <div>
                    <div className="text-[13px] font-semibold text-[var(--color-fg)]">{k.name}</div>
                    <div className="font-mono text-[11px] text-[var(--color-fg-muted)]">{k.id}</div>
                  </div>
                </div>
                <span className={
                  "rounded-full px-2 py-0.5 text-[10.5px] font-medium " +
                  (k.status === "active"
                    ? "bg-[var(--color-success-bg)] text-[var(--color-success)]"
                    : "bg-[var(--color-danger-bg)] text-[var(--color-danger)]")
                }>
                  {k.status === "active" ? "активен" : "отозван"}
                </span>
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-[12px] sm:grid-cols-4">
                <Field label="Выпущен"          value={k.issued} mono />
                <Field label="Действует до"     value={k.expires} mono />
                <Field label="Подписано версий" value={k.signsApps.toLocaleString("ru-RU")} mono />
                <Field label="SHA-256"          value={
                  <CopyableHash hash={k.sha256} onCopy={() => push({ kind: "success", text: "SHA-256 скопирован в буфер" })} />
                } />
              </dl>
            </li>
          ))}
        </ul>
      </section>

      <RotateKeyModal open={rotateOpen} onClose={() => setRotateOpen(false)} activeKeyId={activeKey?.id} />
    </>
  );
}

function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className="mt-2 text-[20px] font-semibold tracking-[-0.01em] text-[var(--color-fg)]">{value}</div>
      <div className="mt-1.5 text-[11.5px] text-[var(--color-fg-muted)]">{sub}</div>
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className={"mt-0.5 text-[var(--color-fg)] " + (mono ? "font-mono text-[12px]" : "")}>{value}</div>
    </div>
  );
}

function CopyableHash({ hash, onCopy }: { hash: string; onCopy: () => void }) {
  const [done, setDone] = useState(false);
  const click = () => {
    navigator.clipboard?.writeText(hash);
    onCopy();
    setDone(true);
    setTimeout(() => setDone(false), 1200);
  };
  return (
    <button onClick={click} className="flex items-center gap-1.5 font-mono text-[11.5px] text-[var(--color-fg)] hover:text-[var(--color-brand)]">
      <span>{hash.slice(0, 18)}…</span>
      {done ? <Check size={11} className="text-[var(--color-success)]" /> : <Copy size={11} className="opacity-60" />}
    </button>
  );
}

function RotateKeyModal({
  open,
  onClose,
  activeKeyId,
}: {
  open: boolean;
  onClose: () => void;
  activeKeyId?: string;
}) {
  const push = useToast();
  const qc = useQueryClient();
  const nextId = nextKeyId(activeKeyId);

  const rotate = useMutation({
    mutationFn: () =>
      rotateKey({
        new_id: nextId,
        new_name: `Корпоративный ключ ${nextId}`,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["keys"] });
      push({ kind: "warn", text: "Ротация ключа: новый ключ выпущен, старый отозван" });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Ошибка ротации") : "Ошибка ротации";
      push({ kind: "warn", text: msg });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<span className="flex items-center gap-2 text-[var(--color-warning)]"><ShieldAlert size={16} />Ротация подписного ключа</span>}
      subtitle="Замена корпоративного ключа на новый"
      size="md"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn
            variant="primary"
            onClick={() => rotate.mutate()}
          >
            {rotate.isPending ? "Идёт ротация…" : "Подтвердить ротацию"}
          </Btn>
        </>
      }
    >
      <p className="text-[13px] text-[var(--color-fg-muted)]">
        Будет выпущен новый корпоративный ключ <span className="font-mono">{nextId}</span>. Текущий ключ
        <span className="font-mono"> {activeKeyId ?? "—"}</span> будет отозван. Каталог переиздастся с подписью нового ключа,
        приложения сохранят свои подписи разработчиков.
      </p>
      <ul className="mt-4 space-y-2 text-[12.5px]">
        <Numbered i={1}>Сгенерировать новый ключ в HSM</Numbered>
        <Numbered i={2}>Переподписать индекс каталога</Numbered>
        <Numbered i={3}>Разослать новый публичный ключ по 14 Локальным серверам</Numbered>
        <Numbered i={4}>Отозвать предыдущий ключ через 7 дней</Numbered>
      </ul>
    </Modal>
  );
}

function nextKeyId(activeKeyId: string | undefined): string {
  // corp-2026-A → corp-2026-B
  if (!activeKeyId) return "corp-2026-B";
  const m = activeKeyId.match(/^(.*-)([A-Z])$/);
  if (!m) return activeKeyId + "-B";
  const next = String.fromCharCode(m[2].charCodeAt(0) + 1);
  return m[1] + next;
}

function Numbered({ i, children }: { i: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--color-brand)] text-[10px] font-semibold text-white">
        {i}
      </span>
      <span className="text-[var(--color-fg)]">{children}</span>
    </li>
  );
}
